import { useCallback, useState } from 'react';
import { AxiosInstance } from 'axios';
import { Usuario, useAuth } from '../../hooks/auth';
import {
  apiHnsn,
  apiLaureano,
  apiLaureanoSoul,
  apiPhato,
  CEDAPP_API_KEY,
} from '../../services/api';
import { useLoader } from '../../hooks/loading';
import {
  getLaudoAcessos,
  postVisualizarLaudo,
} from '../../controller/dashboard';

export type LaudoOrigem = 'PHATO' | 'LAUREANO' | 'LAUREANO_MV2000' | 'HNSN';

export interface LaudoExame {
  cdLaudo: string;
  cdPedRx: string;
  nmPaciente: string;
  dtLaudo: string;
  isRecebimento?: boolean;
  dsExame: string;
  nrControle: string;
  from: LaudoOrigem;
  status: string;
  visualizado: boolean;
  dhVisualizacao?: string;
  medicoRequisitante?: string;
}

export interface LegacyLaudoApiItem {
  cdLaudo: string;
  cdPedRx: string;
  nmPaciente: string;
  dtLaudo?: string;
  dtPedido?: string;
  dsExame: string;
  nrControle: string;
  status: string;
  visualizado: boolean;
  dhVisualizacao?: string;
  medicoRequisitante?: string;
}

export interface LaudoAcesso {
  tpUsuario: string;
  cdUsuario: number;
  nome: string;
  dhVisualizacao: string;
}

export interface PhatoExame {
  id: number;
  numero: string;
  nomePaciente: string;
  dataLiberacao?: string | null;
  dataRecebimento?: string | null;
  tipoExame?: string | null;
  medicoRequisitante?: string;
  crm?: string;
  cpf?: string;
  dataNascimento?: string;
  descricaoExame?: string | null;
  status?: string;
  dhVisualizacao?: string | null;
  visualizado?: boolean;
}

export type PhatoProductivityFiltros = {
  cpf?: string;
  crm?: string;
  nomePaciente?: string;
};

export interface PhatoExamesResponse {
  max: number;
  offset: number;
  exames: PhatoExame[];
}

export interface PhatoExamesMeta {
  max: number;
  offset: number;
}

function formatPhatoDate(value: string | null | undefined) {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return [
    String(date.getUTCDate()).padStart(2, '0'),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    date.getUTCFullYear(),
  ].join('/');
}

export function mapPhatoExame(exame: PhatoExame): LaudoExame {
  const dtLiberacao = formatPhatoDate(exame.dataLiberacao);
  const dtRecebimento = formatPhatoDate(exame.dataRecebimento);
  const hasLiberacao = Boolean(dtLiberacao);

  return {
    cdLaudo: String(exame.id),
    cdPedRx: String(exame.id),
    nmPaciente: exame.nomePaciente,
    dtLaudo: hasLiberacao ? dtLiberacao : dtRecebimento,
    isRecebimento: !hasLiberacao && Boolean(dtRecebimento),
    dsExame: (exame.tipoExame || '').trim(),
    nrControle: exame.numero || '',
    from: 'PHATO',
    status: exame.status || '',
    visualizado: exame.visualizado ?? false,
    dhVisualizacao: exame.dhVisualizacao || undefined,
    medicoRequisitante: exame.medicoRequisitante,
  };
}

export function mapLegacyLaudo(
  item: LegacyLaudoApiItem,
  from: LaudoOrigem,
): LaudoExame {
  const dtLiberacao = (item.dtLaudo || '').trim();
  const dtPedido = (item.dtPedido || '').trim();
  const hasLiberacao = Boolean(dtLiberacao);

  return {
    cdLaudo: item.cdLaudo,
    cdPedRx: item.cdPedRx,
    nmPaciente: item.nmPaciente,
    dtLaudo: hasLiberacao ? dtLiberacao : dtPedido,
    isRecebimento: !hasLiberacao && Boolean(dtPedido),
    dsExame: item.dsExame,
    nrControle: item.nrControle,
    from,
    status: item.status,
    visualizado: item.visualizado,
    dhVisualizacao: item.dhVisualizacao,
    medicoRequisitante: item.medicoRequisitante,
  };
}

export function filterLaudosByNomePaciente(
  laudos: LaudoExame[],
  nomePaciente: string,
) {
  const normalized = nomePaciente.trim().toUpperCase();
  if (!normalized) return laudos;

  return laudos.filter((laudo) =>
    (laudo.nmPaciente || '').toUpperCase().includes(normalized),
  );
}

type UsuarioNomeSource = {
  nome?: string;
  NAME?: string;
} | null;

type PhatoAccessNomeSource = {
  isMedico: boolean;
  crm: string;
  cpf: string;
} | null;

export function resolveUsuarioNome({
  usuarioHnsn,
  usuarioPhato,
  usuarioLaureano,
  usuarioLaureanoSoul,
  phatoAccess,
  phatoLaudos = [],
  isAdmin = false,
}: {
  usuarioHnsn?: UsuarioNomeSource;
  usuarioPhato?: UsuarioNomeSource;
  usuarioLaureano?: UsuarioNomeSource;
  usuarioLaureanoSoul?: UsuarioNomeSource;
  phatoAccess?: PhatoAccessNomeSource;
  phatoLaudos?: LaudoExame[];
  isAdmin?: boolean;
}): string | undefined {
  const storedName = [
    usuarioHnsn,
    usuarioPhato,
    usuarioLaureano,
    usuarioLaureanoSoul,
  ].reduce<string | undefined>(
    (name, user) => name || user?.nome || user?.NAME,
    undefined,
  );
  if (storedName) return storedName;

  if (phatoAccess && !isAdmin && phatoLaudos.length > 0) {
    const name = phatoAccess.isMedico
      ? phatoLaudos.find((laudo) => laudo.medicoRequisitante)
          ?.medicoRequisitante
      : phatoLaudos.find((laudo) => laudo.nmPaciente)?.nmPaciente;
    if (name) return name;
  }

  if (phatoAccess) {
    return phatoAccess.isMedico ? phatoAccess.crm : phatoAccess.cpf;
  }

  return undefined;
}

export function filterPhatoLaudos(laudos: LaudoExame[], filtro: string) {
  const normalizedFilter = filtro.trim().toUpperCase();
  if (!normalizedFilter) return laudos;

  return laudos.filter((laudo) =>
    [laudo.nrControle, laudo.nmPaciente, laudo.dsExame]
      .filter(Boolean)
      .some((value) => value.toUpperCase().includes(normalizedFilter)),
  );
}

function tagOrigin(laudos: LaudoExame[], from: LaudoOrigem) {
  return laudos.map((laudo) => ({ ...laudo, from }));
}

function mapLegacyLaudos(items: LegacyLaudoApiItem[], from: LaudoOrigem) {
  return items.map((item) => mapLegacyLaudo(item, from));
}

export function mergeLaudoSources(
  sources: Array<{ from: LaudoOrigem; laudos?: LaudoExame[] }>,
) {
  return sources.reduce<LaudoExame[]>((allLaudos, source) => {
    if (source.laudos) {
      allLaudos.push(...tagOrigin(source.laudos, source.from));
    }
    return allLaudos;
  }, []);
}

export function getApiForOrigin(from: LaudoOrigem) {
  if (from === 'PHATO') return apiPhato;
  if (from === 'LAUREANO') return apiLaureanoSoul;
  if (from === 'LAUREANO_MV2000') return apiLaureano;
  return apiHnsn;
}

async function fetchBusinessLaudos(
  api: AxiosInstance,
  user: Usuario | null,
  token: string | null,
  filtro?: string,
) {
  if (!user || !token) return [] as LaudoExame[];

  const { data } = await api.get<Array<LaudoExame & { dtPedido?: string }>>(
    `business/${user.CNPJ || ''}/laudos`,
    {
      params: { alias: user.ALIAS || '', filtro },
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data.map((item) => {
    const dtLiberacao = (item.dtLaudo || '').trim();
    const dtPedido = (item.dtPedido || '').trim();
    if (dtLiberacao) {
      return { ...item, dtLaudo: dtLiberacao, isRecebimento: false };
    }
    if (dtPedido) {
      return { ...item, dtLaudo: dtPedido, isRecebimento: true };
    }
    return item;
  });
}

export const useDashboard = () => {
  const {
    usuarioHnsn,
    usuarioPhato,
    usuarioLaureano,
    usuarioLaureanoSoul,
    phatoAccess,
    tokenPhato,
    tokenLaureano,
    tokenLaureanoSoul,
    tokenHnsn,
    signOut,
  } = useAuth();
  const [laudos, setLaudos] = useState<LaudoExame[]>([]);
  const [phatoExamesMeta, setPhatoExamesMeta] = useState<PhatoExamesMeta>({
    max: 0,
    offset: 0,
  });
  const { showLoader, hideLoader } = useLoader();

  const handleGetPhatoProductivity = useCallback(
    async (filtros?: PhatoProductivityFiltros) => {
      if (!phatoAccess || !tokenPhato) {
        setPhatoExamesMeta({ max: 0, offset: 0 });
        return [] as LaudoExame[];
      }

      const body: Record<string, string | number> = { max: 50, offset: 0 };
      const isAdmin =
        phatoAccess.crm === '00009' || phatoAccess.crm === '00000';
      const nomePaciente = String(filtros?.nomePaciente || '').trim();

      if (isAdmin) {
        const crm = String(filtros?.crm || '').replace(/\D/g, '');
        const cpf = String(filtros?.cpf || '').replace(/\D/g, '');
        if (!crm && !cpf && !nomePaciente) {
          setPhatoExamesMeta({ max: 0, offset: 0 });
          return [] as LaudoExame[];
        }
        if (crm) body.crm = crm;
        if (cpf) body.cpf = cpf;
        if (nomePaciente) body.nomePaciente = nomePaciente;
      } else if (phatoAccess.isMedico) {
        const cpfPaciente = String(filtros?.cpf || '').replace(/\D/g, '');
        if (cpfPaciente) body.cpf = cpfPaciente;
        if (nomePaciente) body.nomePaciente = nomePaciente;
      }

      const { data } = await apiPhato.post<PhatoExamesResponse>(
        '/v2/produtividade/exames',
        body,
        { headers: { Authorization: `Bearer ${tokenPhato}` } },
      );

      setPhatoExamesMeta({ max: data.max, offset: data.offset });
      return data.exames.map(mapPhatoExame);
    },
    [phatoAccess, tokenPhato],
  );

  const getLegacyUserLaudos = useCallback(
    async (
      api: AxiosInstance,
      user: Usuario | null,
      from: LaudoOrigem,
      filtro?: string,
    ) => {
      if (!user) return [] as LaudoExame[];

      const endpoint = user.cdPrestadorArray
        ? `/medico/${user.cdPrestadorArray}`
        : '/paciente';
      const { data } = await api.get<LegacyLaudoApiItem[]>(endpoint, {
        params:
          filtro && user.cdPrestadorArray
            ? { filtro: filtro.toUpperCase() }
            : undefined,
      });

      return mapLegacyLaudos(data.slice(0, 200), from);
    },
    [],
  );

  const handleGetLegacyLaudos = useCallback(
    async (filtro?: string) => {
      const results = await Promise.allSettled([
        getLegacyUserLaudos(
          apiLaureano,
          usuarioLaureano,
          'LAUREANO_MV2000',
          filtro,
        ),
        getLegacyUserLaudos(
          apiLaureanoSoul,
          usuarioLaureanoSoul,
          'LAUREANO',
          filtro,
        ),
        getLegacyUserLaudos(apiHnsn, usuarioHnsn, 'HNSN', filtro),
      ]);

      return results.reduce<LaudoExame[]>((allLaudos, result) => {
        if (result.status === 'fulfilled') allLaudos.push(...result.value);
        return allLaudos;
      }, []);
    },
    [getLegacyUserLaudos, usuarioHnsn, usuarioLaureano, usuarioLaureanoSoul],
  );

  const isLegacyPatient = useCallback(
    () =>
      [usuarioLaureano, usuarioLaureanoSoul, usuarioHnsn].some(
        (user) => user && !user.cdPrestadorArray,
      ),
    [usuarioHnsn, usuarioLaureano, usuarioLaureanoSoul],
  );

  const loadBusinessLaudos = useCallback(
    async (filtro?: string) => {
      const results = await Promise.allSettled([
        fetchBusinessLaudos(apiPhato, usuarioPhato, tokenPhato, filtro),
        fetchBusinessLaudos(
          apiLaureano,
          usuarioLaureano,
          tokenLaureano,
          filtro,
        ),
        fetchBusinessLaudos(
          apiLaureanoSoul,
          usuarioLaureanoSoul,
          tokenLaureanoSoul,
          filtro,
        ),
        fetchBusinessLaudos(apiHnsn, usuarioHnsn, tokenHnsn, filtro),
      ]);
      const origins: LaudoOrigem[] = [
        'PHATO',
        'LAUREANO_MV2000',
        'LAUREANO',
        'HNSN',
      ];

      return mergeLaudoSources(
        results.map((result, index) => ({
          from: origins[index],
          laudos: result.status === 'fulfilled' ? result.value : undefined,
        })),
      );
    },
    [
      tokenHnsn,
      tokenLaureano,
      tokenLaureanoSoul,
      tokenPhato,
      usuarioHnsn,
      usuarioLaureano,
      usuarioLaureanoSoul,
      usuarioPhato,
    ],
  );

  const handleGetLaudosBusiness = useCallback(async () => {
    showLoader();
    try {
      setLaudos(await loadBusinessLaudos());
    } finally {
      hideLoader();
    }
  }, [hideLoader, loadBusinessLaudos, showLoader]);

  const handleFilterBusinessLaudos = useCallback(
    async (filtro: string) => {
      showLoader();
      try {
        setLaudos(await loadBusinessLaudos(filtro));
      } finally {
        hideLoader();
      }
    },
    [hideLoader, loadBusinessLaudos, showLoader],
  );

  function getVisualizarConfig(from: LaudoOrigem) {
    if (from !== 'PHATO') return undefined;
    return { headers: { 'x-api-key': CEDAPP_API_KEY } };
  }

  function buildPhatoVisualizarBody(nome: string) {
    if (!phatoAccess) return undefined;

    return {
      documento: phatoAccess.isMedico ? phatoAccess.crm : phatoAccess.cpf,
      tpUsuario: phatoAccess.isMedico ? 'M' : 'P',
      nome,
    };
  }

  async function handleVisualizarLaudo(
    cdLaudo: string,
    from: LaudoOrigem,
    nome?: string,
  ) {
    try {
      await postVisualizarLaudo(
        cdLaudo,
        getApiForOrigin(from),
        from === 'PHATO' ? buildPhatoVisualizarBody(nome || '') : undefined,
        getVisualizarConfig(from),
      );
    } catch (error) {}
  }

  async function handleGetLaudoAcessos(
    cdLaudo: string,
    from: LaudoOrigem,
  ): Promise<LaudoAcesso[]> {
    const config =
      from === 'PHATO' && tokenPhato
        ? { headers: { Authorization: `Bearer ${tokenPhato}` } }
        : undefined;
    const { data } = await getLaudoAcessos(
      cdLaudo,
      getApiForOrigin(from),
      config,
    );
    return data;
  }

  return {
    handleGetLaudosBusiness,
    handleFilterBusinessLaudos,
    handleGetPhatoProductivity,
    handleGetLegacyLaudos,
    isLegacyPatient,
    filterPhatoLaudos,
    phatoAccess,
    phatoExamesMeta,
    usuarioHnsn,
    usuarioPhato,
    usuarioLaureano,
    usuarioLaureanoSoul,
    tokenPhato,
    tokenLaureano,
    tokenLaureanoSoul,
    tokenHnsn,
    signOut,
    laudos,
    setLaudos,
    showLoader,
    hideLoader,
    handleVisualizarLaudo,
    handleGetLaudoAcessos,
  };
};
